import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BellIcon, CheckIcon, CheckBadgeIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { notificationAPI } from '../services/api';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'room_change' | 'booking_update' | 'client_arrived' | 'system_alert';
  is_read: boolean;
  created_at: string;
  booking_id?: number;
}

type TimeRangeFilter = 'today' | 'week' | 'month';

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>('week');
  const [error, setError] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await notificationAPI.getNotifications(timeRange, false, 20);
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  // Fetch unread count only
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.unread_count);
    } catch (error: any) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true }
            : notification
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error: any) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: number) => {
    try {
      await notificationAPI.deleteNotification(notificationId);
      
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Initial load and polling
  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'room_change':
        return <ClockIcon className="h-4 w-4 text-clinic-gold" />;
      case 'booking_update':
        return <CheckIcon className="h-4 w-4 text-clinic-primary" />;
      case 'client_arrived':
        return <CheckBadgeIcon className="h-4 w-4 text-clinic-primary" />;
      default:
        return <BellIcon className="h-4 w-4 text-clinic-muted" />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-clinic-muted hover:text-clinic-primary hover:bg-clinic-surface-sunken focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:ring-offset-2 rounded-full transition-colors"
        aria-label={`Notifications (${unreadCount} unread)`}
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-clinic-accent text-white text-[10px] font-bold rounded-full h-5 min-w-[1.25rem] px-1 flex items-center justify-center ring-2 ring-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-card shadow-clinic-lg border border-clinic-primary-lighter z-50">
          {/* Header */}
          <div className="p-4 border-b border-clinic-primary-lighter">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-clinic-dark font-body">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm font-medium text-clinic-primary hover:text-clinic-primary-dark focus:outline-none"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Time Range Filter */}
            <div className="flex space-x-2">
              {(['today', 'week', 'month'] as TimeRangeFilter[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    timeRange === range
                      ? 'bg-clinic-primary text-clinic-cream'
                      : 'text-clinic-muted hover:bg-clinic-surface-sunken'
                  }`}
                >
                  {range === 'today' ? 'Today' : range === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-clinic-muted">Loading...</div>
            ) : error ? (
              <div className="p-4 text-center text-clinic-accent">{error}</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-clinic-muted">
                No notifications for the selected time range
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b border-clinic-primary-lighter hover:bg-clinic-surface-sunken transition-colors ${
                    !notification.is_read ? 'bg-clinic-primary-lighter/40' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {getNotificationIcon(notification.type)}
                        <h4 className={`text-sm font-medium ${
                          !notification.is_read ? 'text-clinic-dark' : 'text-clinic-muted'
                        }`}>
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-clinic-primary rounded-full"></div>
                        )}
                      </div>
                      <p className="text-sm text-clinic-muted mb-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-clinic-muted">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>

                    <div className="flex items-center space-x-1 ml-2">
                      {!notification.is_read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 text-clinic-muted hover:text-clinic-primary focus:outline-none"
                          title="Mark as read"
                        >
                          <CheckIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 text-clinic-muted hover:text-clinic-accent focus:outline-none"
                        title="Delete"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-clinic-primary-lighter text-center">
              <button className="text-sm font-medium text-clinic-primary hover:text-clinic-primary-dark focus:outline-none">
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;